import { Fragment } from 'react'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import { formatCurrency, formatDate } from '../../../components/utils/formatter'

const styles = {
  table: 'table-report-auto min-w-[1080px]',
  th: 'table-report-th text-left',
  td: 'table-report-td align-top border-b border-border',
  nestedTable: 'w-full text-[11px] border-collapse bg-info/10 rounded-lg shadow-inner font-sans text-text',
  nestedTh: 'py-2 px-3 text-left border-b border-info/20 bg-info/15 font-semibold text-text',
  nestedTd: 'py-2 px-3 align-top border-b border-info/20'
}

const getLinkageDetail = link => ({
  tipoDocumento: link.name ?? link.nombreFolio ?? link.tipoComprobante ?? '-',
  folio: link.numero ?? link.numeroFolio ?? link.comprobante ?? '-',
  rut: link.rut ?? '-',
  razonSocial: link.descripcion ?? link.razonSocial ?? link.nombreCuenta ?? '-',
  fecha: link.fechaPago ?? link.fecha,
  montoDocumento: link.montoTotal ?? link.debe ?? link.haber ?? 0,
  montoSaldado:
    link.monto ??
    link.montoRelacionado ??
    link.montoAbono ??
    link.montoCargo ??
    link.montoTotal ??
    0
})

const DetailLinkages = ({ linkages }) => {
  if (!linkages || linkages.length === 0) {
    return <div className='py-3 text-sm text-muted font-sans'>No hay facturas conciliadas para este movimiento.</div>
  }
  return (
    <div className='overflow-x-auto'>
      <table className={styles.nestedTable}>
        <thead>
          <tr>
            <th className={styles.nestedTh}>Tipo Documento</th>
            <th className={styles.nestedTh}>Folio</th>
            <th className={styles.nestedTh}>Rut</th>
            <th className={styles.nestedTh}>Razón Social</th>
            <th className={styles.nestedTh}>Fecha</th>
            <th className={styles.nestedTh}>Monto Documento</th>
            <th className={styles.nestedTh}>Monto Saldado</th>
          </tr>
        </thead>
        <tbody>
          {linkages.map((link, idx) => {
            const detail = getLinkageDetail(link)
            return (
              <tr key={`${link.id || detail.folio || idx}-${idx}`} className='odd:bg-white even:bg-blue-50/60'>
                <td className={styles.nestedTd}>{detail.tipoDocumento}</td>
                <td className={styles.nestedTd}>
                  <div>{detail.folio}</div>
                </td>
                <td className={styles.nestedTd}>
                  <div>{detail.rut}</div>
                </td>
                <td className={styles.nestedTd}>
                  <div className='text-muted'>{detail.razonSocial}</div>
                </td>
                <td className={styles.nestedTd}>{detail.fecha ? formatDate(detail.fecha) : '-'}</td>
                <td className={styles.nestedTd}>{formatCurrency(detail.montoDocumento)}</td>
                <td className={styles.nestedTd}>{formatCurrency(detail.montoSaldado)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const TableBancoConciliaciones = ({
  rows,
  expandedRows,
  onToggleRow,
  linkageByRow,
  loadingDetails,
  isLoading,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  lastElementRef
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const hasRows = Array.isArray(rows) && rows.length > 0

  const getStatusStyles = status => ({
    backgroundColor:
      status === 'CONCILIADO' ? '#e0f7e9' : status === 'PENDIENTE' ? '#fff4e5' : '#eef2ff',
    color: status === 'CONCILIADO' ? '#0b9e43' : status === 'PENDIENTE' ? '#c05621' : '#312e81'
  })

  if (isLoading && !hasRows) {
    return (
      <div className='flex items-center justify-center py-12 text-muted text-sm font-sans'>
        Cargando movimientos bancarios...
      </div>
    )
  }

  if (!hasRows) {
    return (
      <div className='border border-border rounded-2xl shadow-sm overflow-hidden bg-surface font-sans'>
        <div className='flex flex-col items-center justify-center py-12 text-muted text-sm'>
          <SearchIcon className='mb-2 text-muted' />
          No hay movimientos para los filtros seleccionados.
        </div>

        {hasNextPage && (
          <div className='flex justify-center items-center py-3'>
            <button
              className='px-4 py-2 rounded-full bg-surface border border-border text-sm hover:bg-border/30 disabled:opacity-50'
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Cargando...' : 'Cargar más'}
            </button>
          </div>
        )}
      </div>
    )
  }

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className='rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0'>
        <div className='text-[11px] text-muted'>{label}</div>
        <div className='text-sm font-semibold text-text break-words'>{value}</div>
      </div>
    )

    return (
      <div className='space-y-3 font-sans text-text'>
        {rows.map(row => {
          const isExpanded = expandedRows.has(row.id)
          const linkages = linkageByRow[row.id] ?? row.linkage ?? []
          const count = Array.isArray(linkages) ? linkages.length : 0
          const showBadge = (row.status === 'CONCILIADO' && count > 0) || count > 1
          return (
            <div key={row.id} className='rounded-2xl border border-border bg-surface p-3'>
              <div className='flex items-start justify-between gap-2'>
                <div>
                  <div className='text-xs font-semibold text-text'>
                    {row.fecha ? formatDate(row.fecha) : '-'}
                  </div>
                  <div className='text-[11px] text-muted'>
                    {row.nombreCuentaBanco || '-'} {row.numeroCuentaBanco ? `· ${row.numeroCuentaBanco}` : ''}
                  </div>
                </div>
                <button
                  className='relative flex items-center justify-center w-9 h-9 rounded-lg bg-border/60 text-text hover:bg-border transition shadow-sm'
                  onClick={() => onToggleRow(row)}
                  aria-label={isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                >
                  {showBadge && (
                    <span className='absolute -top-1 -right-1 min-w-[18px] h-[18px] px-[5px] rounded-full bg-accent2 text-white text-[10px] font-semibold leading-none flex items-center justify-center'>
                      {count}
                    </span>
                  )}
                  {isExpanded ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
                </button>
              </div>

              <div className='mt-2 text-[11px] text-muted'>
                {row.descripcion || '-'}
              </div>
              <div className='text-[11px] text-muted'>
                {row.categoriaTipoMovimiento || ''}
              </div>

              <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                {renderMetric('Abono', formatCurrency(row.montoAbono || 0))}
                {renderMetric('Cargo', formatCurrency(row.montoCargo || 0))}
                <div className='rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0'>
                  <div className='text-[11px] text-muted'>Estado</div>
                  <span
                    className='px-2 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1'
                    style={getStatusStyles(row.status)}
                  >
                    {row.status || '-'}
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className='mt-3 rounded-xl border border-info/20 bg-info/10 p-3'>
                  {loadingDetails[row.id] ? (
                    <div className='text-sm text-muted'>Cargando facturas conciliadas...</div>
                  ) : (
                    <div className='space-y-2'>
                      {Array.isArray(linkages) && linkages.length > 0 ? (
                        linkages.map((link, idx) => {
                          const detail = getLinkageDetail(link)
                          return (
                            <div
                              key={`${link.id || detail.folio || idx}-${idx}`}
                              className='rounded-lg bg-surface p-2 border border-info/20'
                            >
                              <div className='text-[11px] font-semibold text-text'>
                                {detail.tipoDocumento} · {detail.folio}
                              </div>
                              <div className='text-[11px] text-muted'>
                                {detail.rut} {detail.razonSocial}
                              </div>
                              <div className='text-[11px] text-muted'>
                                {detail.fecha ? formatDate(detail.fecha) : '-'}
                              </div>
                              <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                                {renderMetric(
                                  'Monto Documento',
                                  formatCurrency(detail.montoDocumento)
                                )}
                                {renderMetric('Monto Saldado', formatCurrency(detail.montoSaldado))}
                              </div>
                            </div>
                          )
                        })
                      ) : (
                        <div className='text-sm text-muted'>
                          No hay facturas conciliadas para este movimiento.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {hasNextPage && (
          lastElementRef ? (
            <div ref={lastElementRef} className='flex justify-center items-center py-4 text-sm text-muted'>
              {isFetchingNextPage ? 'Cargando...' : 'Desplázate para cargar más'}
            </div>
          ) : (
            <div className='flex justify-center items-center py-3'>
              <button
                className='px-4 py-2 rounded-full bg-surface border border-border text-sm hover:bg-border/30 disabled:opacity-50'
                onClick={onLoadMore}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Cargando...' : 'Cargar más'}
              </button>
            </div>
          )
        )}
      </div>
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col border border-border rounded-2xl shadow-sm overflow-hidden bg-surface font-sans text-text'>
      <div className='min-h-0 flex-1 overflow-x-auto'>
        <div className='h-full overflow-y-auto'>
        <table className={styles.table}>
          <thead className='sticky top-0 z-10'>
            <tr>
              <th className={`${styles.th} w-[110px]`}>Fecha</th>
              <th className={`${styles.th} w-[130px]`}>Banco</th>
              <th className={`${styles.th} w-[160px]`}>Cuenta</th>
              <th className={`${styles.th} w-[260px]`}>Descripción</th>
              <th className={`${styles.th} w-[120px] text-right`}>Abono</th>
              <th className={`${styles.th} w-[120px] text-right`}>Cargo</th>
              <th className={`${styles.th} w-[170px]`}>Status Conciliación</th>
              <th className={`${styles.th} w-[70px]`}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isExpanded = expandedRows.has(row.id)
              const linkages = linkageByRow[row.id] ?? row.linkage ?? []
              const count = Array.isArray(linkages) ? linkages.length : 0
              // Mostrar badge si está conciliado (cualquier cantidad) o si tiene más de un movimiento
              const showBadge = (row.status === 'CONCILIADO' && count > 0) || count > 1

              return (
              <Fragment key={row.id}>
                <tr className='table-report-row odd:bg-surface even:bg-border/20'>
                  <td className={`${styles.td} whitespace-nowrap`}>{row.fecha ? formatDate(row.fecha) : '-'}</td>
                  <td className={styles.td}>
                    <div className='text-[12px] max-w-[130px] whitespace-normal break-words leading-tight'>{row.nombreCuentaBanco || '-'}</div>
                  </td>
                  <td className={styles.td}>
                    <div className='text-muted text-[11px] max-w-[160px] whitespace-normal break-words leading-tight'>{row.numeroCuentaBanco || ''}</div>
                  </td>
                  <td className={styles.td}>
                    <div className='text-[12px] max-w-[260px] whitespace-normal break-words leading-tight'>{row.descripcion || '-'}</div>
                    <div className='text-muted text-[11px] max-w-[260px] whitespace-normal break-words leading-tight'>{row.categoriaTipoMovimiento || ''}</div>
                  </td>
                  <td className={`${styles.td} text-right whitespace-nowrap`}>{formatCurrency(row.montoAbono || 0)}</td>
                  <td className={`${styles.td} text-right whitespace-nowrap`}>{formatCurrency(row.montoCargo || 0)}</td>
                  <td className={styles.td}>
                    <span
                      className='px-2 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1'
                      style={getStatusStyles(row.status)}
                    >
                      {row.status || '-'}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <button
                      className='relative flex items-center justify-center w-9 h-9 rounded-lg bg-border/60 text-text hover:bg-border transition shadow-sm'
                      onClick={() => onToggleRow(row)}
                      aria-label={isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                    >
                      {showBadge && (
                        <span className='absolute -top-1 -right-1 min-w-[18px] h-[18px] px-[5px] rounded-full bg-accent2 text-white text-[10px] font-semibold leading-none flex items-center justify-center'>
                          {count}
                        </span>
                      )}
                      {isExpanded ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
                    </button>
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={8} className='bg-info/10 px-3 py-3 border-l-4 border-info/20'>
                      {loadingDetails[row.id] ? (
                        <div className='text-sm text-muted'>Cargando facturas conciliadas...</div>
                      ) : (
                        <DetailLinkages
                          linkages={Array.isArray(linkages) ? linkages : []}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          </tbody>
        </table>

        {hasNextPage && (
          lastElementRef ? (
            <div ref={lastElementRef} className='flex justify-center items-center py-4 text-sm text-muted'>
              {isFetchingNextPage ? 'Cargando...' : 'Desplázate para cargar más'}
            </div>
          ) : (
            <div className='flex justify-center items-center py-3'>
              <button
                className='px-4 py-2 rounded-full bg-surface border border-border text-sm hover:bg-border/30 disabled:opacity-50'
                onClick={onLoadMore}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Cargando...' : 'Cargar más'}
              </button>
            </div>
          )
        )}
        </div>
      </div>
    </div>
  )
}

export default TableBancoConciliaciones
